import { useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import PageHero from "@/components/common/PageHero";
import gen1 from "@/assets/images/keshavlal.png";
import gen2 from "@/assets/images/narendra.png";
import gen3 from "@/assets/images/ketan.png";
import gen4 from "@/assets/images/Raunaq.jpg";
import factory from "@/assets/images/factory.png";

const highlights = [
  "Dedicated B2B support for procurement teams and marketing managers",
  "In-house branding coordination from logo mockup to production",
  "Flexible bulk planning aligned to campaign and seasonal demand",
  "Structured dispatch coordination for multi-city delivery requirements",
];

const founder = {
  name: "Keshavlal Bhailal Vasani",
  title: "Founder, Keshavlal & Company",
  description:
    "The company foundation was built by Keshavlal Bhailal Vasani with a deep focus on durable umbrella craftsmanship and dependable manufacturing values that continue to guide every generation.",
};

const generationTimeline = [
  {
    slug: "generation-1",
    title: "Generation 1",
    personName: "Keshavlal Bhailal Vasani",
    subtitle: "Foundation Era",
    image: gen1,
  },
  {
    slug: "generation-2",
    title: "Generation 2",
    personName: "Narendra Kumar Vasani",
    subtitle: "Scale Expansion",
    image: gen2,
  },
  {
    slug: "generation-3",
    title: "Generation 3",
    personName: "Ketan Narendra Vasani",
    subtitle: "Process Modernization",
    image: gen3,
  },
  {
    slug: "generation-4",
    title: "Generation 4",
    personName: "Raunaq Ketan Vasani",
    subtitle: "Corporate Branding Leadership",
    image: gen4,
  },
];

const qualityCommitment = [
  "Material and frame quality checks at each production stage",
  "Consistency-focused batch manufacturing for bulk orders",
  "Durability-first umbrella assembly and stitching standards",
  "Reliable nationwide dispatch planning for corporate requirements",
];

const AboutPage = () => {
  useEffect(() => {
    document.title = "About Us | Keshavalal & Company";
  }, []);

  return (
    <div data-testid="about-page-root">
      <PageHero
        eyebrow="About Keshavlal & Company"
        title="Legacy Manufacturing, Modern Corporate Supply"
        description="Founded decades ago, Keshavlal & Company has expanded through generations to serve both retail and corporate umbrella markets with manufacturing-first reliability."
        testId="about-page-hero"
      />

      <section className="border-b border-slate-200 bg-white" data-testid="about-story-section">
        <div className="mx-auto grid w-full max-w-6xl gap-8 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div className="space-y-4" data-testid="about-story-content">
            <h2 className="text-3xl font-semibold text-slate-900" data-testid="about-story-title">
              Four Generations of Manufacturing Commitment
            </h2>
            <p className="text-base leading-relaxed text-slate-700 md:text-lg" data-testid="about-story-paragraph-1">
              Our journey began with a local focus on dependable umbrellas and evolved into a structured
              manufacturing setup supplying wholesalers, distributors, and institutional buyers.
            </p>
            <p className="text-base leading-relaxed text-slate-700 md:text-lg" data-testid="about-story-paragraph-2">
              Today, our facility supports both our own branded umbrellas and custom logo printed umbrella
              requirements for procurement teams, dealerships, and campaign planners.
            </p>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200" data-testid="about-story-image-wrapper">
            <img
              src={factory}
              alt="Manufacturing team"
              className="safe-media-image"
              data-testid="about-story-image"
            />
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-slate-50" data-testid="about-founder-section">
        <div className="mx-auto grid w-full max-w-6xl gap-8 px-4 py-14 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
          <div className="space-y-4" data-testid="about-founder-content">
            <p
              className="inline-flex rounded-full border border-slate-300 bg-white px-4 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-600"
              data-testid="about-founder-eyebrow"
            >
              Founder Profile
            </p>
            <h2 className="text-3xl font-semibold text-slate-900" data-testid="about-founder-name">
              {founder.name}
            </h2>
            <p className="text-sm font-semibold uppercase tracking-[0.08em] text-orange-600" data-testid="about-founder-title">
              {founder.title}
            </p>
            <p className="text-base leading-relaxed text-slate-700 md:text-lg" data-testid="about-founder-description">
              {founder.description}
            </p>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="about-founder-image-wrapper">
            <img
              src={gen1}
              alt="Founder placeholder"
              className="safe-media-image"
              data-testid="about-founder-image"
            />
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white" data-testid="about-generation-timeline-section">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-semibold text-slate-900" data-testid="about-generation-timeline-title">
            Legacy Timeline Across Generations
          </h2>
          
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="about-generation-timeline-grid">
            {generationTimeline.map((item) => (
              <article
                key={item.slug}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50"
                data-testid={`about-generation-card-${item.slug}`}
              >
                <img
                  src={item.image}
                  alt={`${item.title} placeholder`}
                  className="safe-media-card"
                  data-testid={`about-generation-image-${item.slug}`}
                />
                <div className="space-y-1 px-4 pb-4">
                  <h3 className="text-lg font-semibold text-slate-900" data-testid={`about-generation-title-${item.slug}`}>
                    {item.title}
                  </h3>
                  <p className="text-sm font-semibold text-slate-800" data-testid={`about-generation-person-name-${item.slug}`}>
                    {item.personName}
                  </p>
                  <p className="text-sm text-slate-600" data-testid={`about-generation-subtitle-${item.slug}`}>
                    {item.subtitle}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white" data-testid="about-quality-commitment-section">
        <div className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-semibold text-slate-900" data-testid="about-quality-commitment-title">
            Quality Commitment
          </h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="about-quality-commitment-grid">
            {qualityCommitment.map((item, index) => (
              <Card key={item} className="rounded-2xl border-slate-200" data-testid={`about-quality-card-${index}`}>
                <CardContent className="p-5">
                  <p className="text-sm text-slate-700 md:text-base" data-testid={`about-quality-text-${index}`}>
                    {item}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50" data-testid="about-highlights-section">
        <div className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="mb-6 space-y-2" data-testid="about-highlights-header">
            <h2 className="text-3xl font-semibold text-slate-900" data-testid="about-highlights-title">
              Commercial Capabilities
            </h2>
            <p className="text-base text-slate-600" data-testid="about-highlights-description">
              Distinct business strengths beyond manufacturing quality, built for corporate and wholesale operations.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2" data-testid="about-highlights-grid">
            {highlights.map((point, index) => (
              <Card key={point} className="rounded-2xl border-slate-200" data-testid={`about-highlight-card-${index}`}>
                <CardContent className="p-5">
                  <p className="text-sm font-medium text-slate-700 md:text-base" data-testid={`about-highlight-text-${index}`}>
                    {point}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;